import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AssessIcon from "@mui/icons-material/PlaylistAdd";
import { emphasize } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, lighten, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { capitalizeAllWords, getUserDescription } from "../../utils/helpers";
import OmniAvatar from "../ui/omniAvatar";
import OmniEditButton from "../ui/omniEditButton";

const getPatientInitials = patientODM => {
  let theReturn =
    patientODM.omniData.givenName.charAt(0) + patientODM.omniData.familyName.charAt(0);

  return theReturn.toUpperCase();
};

const getPatientName = (patientODM, proxyPatientODM) => {
  if (patientODM == null) {
    return "unknown";
  }
  let name = patientODM.omniData.displayName
    ? patientODM.omniData.displayName
    : `${patientODM.omniData.givenName} ${patientODM.omniData.familyName}`;
  name = capitalizeAllWords(name);
  if (proxyPatientODM != null && proxyPatientODM._id === patientODM._id) {
    name = "Me (" + name + ")";
  }

  return name;
};

const OmniUserListItem = props => {
  const {
    patientODM,
    proxyPatientODM = null,
    onClickHandler = null,
    onAssessHandler = null,
    onNavigateHandler = null,
  } = props;
  const theme = useTheme();

  return (
    <ListItem
      key={`candidate${patientODM._id}`}
      disableGutters={true}
      sx={{
        padding: theme.spacing(1),
        paddingTop: 0,
        paddingBottom: 0,
        marginBottom: theme.spacing(1),
        borderRadius: theme.spacing(1),
        backgroundColor: alpha(theme.palette.info.main, 0.75),
        color: theme.palette.info.contrastText,
        cursor: "pointer",
        boxShadow: theme.shadows[3],
        userSelect: "none",
        webkitUserSelect: "none",
        "&:hover": {
          backgroundColor: emphasize(theme.palette.info.main, 0.35),
          cursor: "pointer",
        },
      }}
      onClick={e => {
        e.stopPropagation();
        onClickHandler();
      }}
    >
      <Tooltip title={"No Notes"} placement="right">
        <OmniAvatar
          sx={{
            width: 40,
            height: 40,
            fontSize: "1.2rem",
            color: theme.palette.info.main,
            background: theme.palette.info.contrastText,
          }}
        >
          {getPatientInitials(patientODM)}
        </OmniAvatar>
      </Tooltip>
      <ListItemText
        primary={
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              flexDirection: "column",
              marginLeft: theme.spacing(1),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: theme.palette.info.contrastText,
              marginTop: 0,
            }}
          >
            {getPatientName(patientODM, proxyPatientODM)}
          </Typography>
        }
        secondary={
          <Typography
            variant="body2"
            sx={{
              display: "flex",
              flexDirection: "row",
              marginLeft: theme.spacing(1),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginTop: "-6px",
              color: theme.palette.info.contrastText,
              minimumHeight: 20,
            }}
          >
            {getUserDescription(patientODM)}
          </Typography>
        }
      />
      {onAssessHandler && (
        <OmniEditButton
          title={"Give Assessment"}
          placement={"left"}
          onClick={e => {
            e.stopPropagation();
            onAssessHandler(patientODM);
          }}
          icon={<AssessIcon fontSize="large" />}
          sx={{
            position: "absolute",
            top: theme.spacing(1),
            right: theme.spacing(1),
            height: 42,
            width: 42,
            color: theme.palette.info.contrastText,
            background: lighten(theme.palette.info.main, 0.15),
            border: `2px solid ${theme.palette.info.contrastText}`,
            "&:hover": {
              background: theme.palette.commonground.main,
              border: `2px solid ${theme.palette.secondary.main}}`,
            },
          }}
        />
      )}
      {onNavigateHandler && (
        <OmniEditButton
          title={"Manage"}
          placement={"left"}
          onClick={e => {
            e.stopPropagation();
            onNavigateHandler(patientODM);
          }}
          icon={<OpenInNewIcon fontSize="medium" />}
          sx={{
            position: "absolute",
            top: theme.spacing(1),
            right: theme.spacing(1),
            height: 42,
            width: 42,
            color: theme.palette.info.contrastText,
            background: lighten(theme.palette.info.main, 0.15),
            border: `2px solid ${theme.palette.info.contrastText}`,
            "&:hover": {
              background: theme.palette.commonground.main,
              border: `2px solid ${theme.palette.secondary.main}}`,
            },
          }}
        />
      )}
    </ListItem>
  );
};

export default OmniUserListItem;
